import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Package } from 'lucide-react';
import moment from 'moment';
import { useStore } from '../../context/StoreContext';
import { useStorefrontTheme } from '../../shared/theme/storefrontTheme';
import {
  ORDER_SUPPORT_EMAIL,
  orderCommissionLine,
  commissionDisclosureEnabled,
} from '../../utilities/complianceCopy';
import Button from '../../shared/components/atoms/Button';
import Skeleton from '../../shared/components/atoms/Skeleton';
import axios from '../../utilities/axios';
import { deliveryEstimate } from '../../utilities/deliveryEstimate';
import * as API_END_POINT from '../../utilities/constant';
import '../../components/CartView/cart.css';
import '../../shared/components/organisms/Browse/browse.css'; // .br-wrap / .br-crumb / .br-empty
import './orderConfirmation.css';

/* ORDER CONFIRMED — SP-main `order-confirmed.html`.
   ⚠ IT READS THE SAVED ORDER, NOT THE CART OR CONTEXT. `?order=` carries the
   `order_id` place-order returns (the orders PK `/store/orders/:id` looks up,
   not the per-retailer `retailer_order_id`), so the page survives a refresh
   and the link can be shared. ⛔ No tracking, carrier or
   delivery date: nothing has shipped yet. */

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const STATUS_LABEL = {
  unfulfilled: 'Preparing',
  fulfilled: 'Shipped',
  delivered: 'Delivered',
  canceled: 'Cancelled',
  partially_fulfilled: 'Partly shipped',
  partially_fulfilled_and_unfulfilled: 'Partly shipped',
  partially_fulfilled_and_cancelled: 'Partly shipped',
};

// One parcel per brand order; a multi-brand store ships under the vendor's name.
const toParcel = (ob) => {
  const items = ob.order_brand_items || [];
  const vendor = ob.brand_user?.is_multi_brand ? items.find((i) => i.shopdot_vendor_display_name) : null;
  return {
    id: ob.id,
    name: vendor?.shopdot_vendor_display_name || ob.store_name || ob.brand_user?.brand_details?.store_name,
    logo: vendor?.shopdot_vendor_logo_url || ob.brand_user?.brand_details?.store_logo,
    eta: deliveryEstimate(ob.brand_user?.shipping_address),
    status: STATUS_LABEL[ob.fulfillment_status] || 'Preparing',
    items: items.map((i) => ({
      id: i.id,
      productId: i.product_id,
      name: i.product_title,
      variant: i.variant_title && i.variant_title !== 'Default Title' ? i.variant_title : '',
      qty: i.quantity,
      line: (Number(i.msrp_per_item) || 0) * (Number(i.quantity) || 0),
      img: i.variant_image,
    })),
    subtotal: Number(ob.msrp_sub_total) || 0,
    shipping: Number(ob.shipping_cost_sub_total) || 0,
    tax: Number(ob.tax_amount_sub_total) || 0,
    discount: Number(ob.discount_sub_total) || 0,
  };
};

const BrandMark = ({ logo, name }) => {
  const [broken, setBroken] = useState(false);
  if (logo && !broken) return <img src={logo} alt="" onError={() => setBroken(true)} />;
  return <span className="oc-sinit">{String(name || '?').trim().charAt(0).toUpperCase()}</span>;
};

// A saved variant image can outlive its file; fall back to the placeholder.
const ItemThumb = ({ src }) => {
  const [broken, setBroken] = useState(false);
  return src && !broken
    ? <img src={src} alt="" loading="lazy" onError={() => setBroken(true)} />
    : <span className="ph"><Package /></span>;
};

const OrderConfirmation = () => {
  const { pageDetails, storeDetails, storeOrderStatus } = useStore();
  const sfxRef = useStorefrontTheme(pageDetails?.page_data?.colorAndApperances);
  const [searchParams] = useSearchParams();
  /* SHOPDOT-2831: the ShopDot checkout domain returns here with
     `?order_ids=<id>` — this backend's order-details route takes the numeric
     id (Joi.number); `?order=<retailer_order_id>` needs the code lookup that
     only feat/cp-widget-portal-routes has so far. Either resolves to the same
     response, so the page reads whichever it was given. */
  const code = (
    searchParams.get('order') ||
    (searchParams.get('order_ids') || '').split(',')[0] ||
    ''
  ).trim();
  const [orders, setOrders] = useState(null); // null = loading, [] = not found

  useEffect(() => {
    if (!code) { setOrders([]); return undefined; }
    let live = true;
    setOrders(null);
    axios.post(API_END_POINT.STORE_ORDER_DETAILS(encodeURIComponent(code)), {})
      .then((res) => { if (live) setOrders(Array.isArray(res.data?.data) ? res.data.data : []); })
      .catch(() => { if (live) setOrders([]); });
    return () => { live = false; };
  }, [code]);

  const storeName = storeDetails?.store_name || pageDetails?.page_data?.logoIdentity?.storeName || 'The store';

  if (orders === null) {
    return (
      <div ref={sfxRef} className="sfx">
        <div className="br-wrap oc-wrap" aria-busy="true">
          <div className="oc-hero">
            <Skeleton style={{ width: 56, height: 56, borderRadius: '50%', margin: '0 auto 18px' }} />
            <Skeleton style={{ width: 360, maxWidth: '80%', height: 34, margin: '0 auto' }} />
            <Skeleton style={{ width: 300, maxWidth: '70%', height: 16, margin: '14px auto 0' }} />
          </div>
          <div className="oc-cols">
            <div className="oc-main"><Skeleton style={{ height: 180, borderRadius: 14 }} /></div>
            <aside className="oc-side"><Skeleton style={{ height: 200, borderRadius: 14 }} /></aside>
          </div>
        </div>
      </div>
    );
  }

  if (!orders.length) {
    return (
      <div ref={sfxRef} className="sfx">
        <div className="br-wrap">
          <nav className="br-crumb" aria-label="Breadcrumb"><Link to="/">Home</Link> / Order</nav>
          <div className="br-empty oc-missing">
            <h3>We can&rsquo;t find that order</h3>
            <p>The link may be incomplete. If you&rsquo;ve just ordered, your confirmation email has the details.</p>
            <Button href="/">Back to the storefront</Button>
          </div>
        </div>
      </div>
    );
  }

  const first = orders[0];
  const parcels = orders.flatMap((o) => o.order_brands || []).map(toParcel);
  const sum = (key) => parcels.reduce((s, p) => s + p[key], 0);
  const subtotal = sum('subtotal');
  const shipping = sum('shipping');
  const tax = sum('tax');
  const discount = sum('discount');
  const total = subtotal + shipping + tax - discount;

  const addr = first.order_shipping_addresses?.[0] || {};
  const addressLines = [
    first.customer_name || [first.firstname, first.lastname].filter(Boolean).join(' '),
    addr.shipping_address_1,
    addr.shipping_address_2,
    [addr.shipping_city, addr.shipping_state, addr.shipping_zipcode].filter(Boolean).join(', '),
    addr.shipping_country,
  ].filter((x) => x && String(x).trim());

  // Only right after checkout: brands whose part of the order did not go through.
  const failed = (storeOrderStatus || []).filter((o) => o.status === 'failure');

  return (
    <div ref={sfxRef} className="sfx">
      <div className="br-wrap oc-wrap">
        <div className="oc-hero">
          <span className="oc-tick" aria-hidden="true"><Check /></span>
          <h1>Thank you &mdash; your order is in</h1>
          <p className="oc-sub">
            Order <span className="oc-no">{first.retailer_order_id}</span> &middot; a confirmation is on its way to {first.email || 'your email'}
          </p>
        </div>

        {failed.length > 0 && (
          <div className="oc-partial" role="status">
            We couldn&rsquo;t place the part of your order from {failed.map((f) => f.brandStore).join(', ')}. You haven&rsquo;t been charged for it.
          </div>
        )}

        <div className="oc-cols">
          <div className="oc-main">
            <h2 className="oc-h2">{parcels.length === 1 ? 'Your parcel' : `Your ${parcels.length} parcels`}</h2>
            <p className="oc-lede">
              Each brand ships directly to you, so these may arrive on different days. You&rsquo;ll get tracking for each one as it goes out.
            </p>

            {parcels.map((p, i) => (
              <section className="oc-ship" key={p.id}>
                <header className="oc-shead">
                  <span className="oc-smark"><BrandMark logo={p.logo} name={p.name} /></span>
                  <div>
                    <div className="oc-sname">{p.name}</div>
                    <div className="oc-smeta">Parcel {i + 1} of {parcels.length}{p.eta ? ` · ${p.eta}` : ''}</div>
                  </div>
                  <span className="oc-sstatus">{p.status}</span>
                </header>
                <div className="oc-sitems">
                  {p.items.map((l) => (
                    <Link className="oc-si" to={`/product/${l.productId}`} key={l.id}>
                      <span className="oc-sithumb">
                        <ItemThumb src={l.img} />
                      </span>
                      <span className="oc-sitext">
                        <span className="oc-siname">{l.name}</span>
                        <span className="oc-siqty">{l.variant ? `${l.variant} · ` : ''}Qty {l.qty}</span>
                      </span>
                      <span className="oc-siprice">{money(l.line)}</span>
                    </Link>
                  ))}
                </div>
              </section>
            ))}

            <div className="oc-next">
              <h2 className="oc-h2">What happens next</h2>
              <ol className="oc-steps">
                <li><span className="oc-steptitle">Each brand prepares its parcel</span>{storeName} is notified of the order at the same time you are.</li>
                <li><span className="oc-steptitle">Tracking arrives per parcel</span>You get an email as each one ships, not one email for the whole order.</li>
                <li>
                  <span className="oc-steptitle">Returns and refunds go through ShopDot</span>
                  Returns, refunds, and cancellations are handled by ShopDot, not by the brand. You have 30 days from delivery to start a return, and that window is the same for every brand. Full details: <a href="https://legal.shopdotapp.com/shipping-and-returns-policy/" target="_blank" rel="noopener noreferrer">Shipping and Returns</a>.
                </li>
              </ol>
            </div>
          </div>

          <aside className="oc-side">
            <div className="oc-card">
              <h3>Order summary</h3>
              <div className="ct-srow"><span>Subtotal</span><span>{money(subtotal)}</span></div>
              <div className="ct-srow"><span>Shipping</span><span>{shipping ? money(shipping) : 'Free'}</span></div>
              {discount > 0 && <div className="ct-srow"><span>Discount</span><span>&minus;{money(discount)}</span></div>}
              <div className="ct-srow"><span>Tax</span><span>{money(tax)}</span></div>
              <div className="ct-srow is-total"><span>Total</span><span>{money(total)}</span></div>
            </div>
            {addressLines.length > 0 && (
              <div className="oc-card">
                <h3>Delivering to</h3>
                <p className="oc-addr">
                  {addressLines.map((line, i) => <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>)}
                </p>
              </div>
            )}
            <div className="oc-card">
              <h3>Order details</h3>
              <div className="ct-srow"><span>Order number</span><span>{first.retailer_order_id}</span></div>
              <div className="ct-srow"><span>Placed</span><span>{moment(first.created_at).format('MM/DD/YYYY')}</span></div>
              <div className="ct-srow"><span>Delivery</span><span>{first.order_brands?.[0]?.shipping_method || 'Standard'}</span></div>
            </div>
            <div className="oc-card">
              <h3>Sold by ShopDot, Inc.</h3>
              <p className="oc-addr">
                ShopDot, Inc. is the seller and merchant of record for this order.
                {orderCommissionLine(storeName, commissionDisclosureEnabled(storeDetails)) && (
                  <> {orderCommissionLine(storeName, commissionDisclosureEnabled(storeDetails))}</>
                )}
              </p>
              <p className="oc-addr">
                Questions about your order, a return, or a refund:{' '}
                <a href={`mailto:${ORDER_SUPPORT_EMAIL}`}>{ORDER_SUPPORT_EMAIL}</a>
              </p>
            </div>
            <Button href="/all-products" variant="ghost" className="oc-keep">Continue shopping</Button>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
